import { useEffect, useState, useRef } from 'react';
import { useAuthStore, getRefreshTokenFromCookie } from './authStore';
import { API_CONFIG } from '../../constants';

interface AuthCheckProps {
  children: React.ReactNode;
}

interface AuthStorageData {
  access_token: string;
  username: string;
  user_type: 'admin' | 'agent' | 'user';
}

/**
 * Simple Authentication Check Component
 * Check localStorage auth-storage, validate with server, and handle authentication
 */
export function AuthCheck({ children }: AuthCheckProps) {
  const [isChecking, setIsChecking] = useState(true);
  const hasRunRef = useRef(false);
  const { login, logout } = useAuthStore();

  useEffect(() => {
    // Prevent double execution in Strict Mode using ref instead of state
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    let isMounted = true;

    const checkAuthentication = async () => {
      try {
        // Step 1: Check localStorage for auth-storage
        const authStorageData = localStorage.getItem('auth-storage');

        if (!authStorageData) {
          logout();
          return;
        }

        // Step 2: Parse and validate auth-storage structure
        let parsedData: AuthStorageData;
        try {
          parsedData = JSON.parse(authStorageData);
        } catch (parseError) {
          console.error('Failed to parse auth-storage:', parseError);
          localStorage.removeItem('auth-storage');
          logout();
          return;
        }

        // Check if auth-storage has required fields
        if (!parsedData.access_token || !parsedData.username || !parsedData.user_type) {
          console.error('Invalid auth-storage format');
          localStorage.removeItem('auth-storage');
          logout();
          return;
        }

        // Step 3: Send checkUser request to server
        const response = await fetch(API_CONFIG.BASE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${parsedData.access_token}`,
          },
          body: JSON.stringify({
            action: 'checkUser',
            data: {
              username: parsedData.username,
              user_type: parsedData.user_type
            }
          })
        });

        if (!response.ok) {
          console.error('Server check failed');
          if (response.status === 401) {
            localStorage.removeItem('auth-storage');
            logout();
          }
          return;
        }

        const result = await response.json();

        // Step 4: Handle server response
        if (result.success && result.data && result.data.valid === true) {
          // Create user object from server response
          const updatedUser = {
            id: result.data.id || 0,
            username: parsedData.username,
            type: result.data.user_type || parsedData.user_type,
            balance: result.data.balance || 0,
            reserved_balance: result.data.reserved_balance || 0,
            debt_balance: result.data.debt_balance || 0,
            credit_limit: result.data.credit_limit || 0,
            status: result.data.status || 'active',
            agent_id: result.data.agent_id || null,
            agent_name: result.data.agent_name || null,
            created_at: result.data.created_at || '',
            last_login_at: result.data.last_login_at || null
          };

          // Handle token refresh if needed
          let finalToken = parsedData.access_token;
          if (result.data.action === 'TOKEN_REFRESHED' && result.data.tokens) {
            finalToken = result.data.tokens.access_token;
            // Update store with new tokens
            const { setTokens } = useAuthStore.getState();
            setTokens(result.data.tokens);
          }

          // Update localStorage with new token if changed
          if (finalToken !== parsedData.access_token) {
            const persistData = {
              access_token: finalToken,
              username: updatedUser.username,
              user_type: updatedUser.type
            };
            localStorage.setItem('auth-storage', JSON.stringify(persistData));
          }

          // Login with updated data
          login(updatedUser, finalToken);
        } else {
          console.error('Invalid session response:', result);
          localStorage.removeItem('auth-storage');
          logout();
        }

      } catch (error) {
        console.error('Authentication check error:', error);
        logout();
      } finally {
        if (isMounted) {
          setIsChecking(false);
        }
      }
    };

    checkAuthentication();

    return () => {
      isMounted = false;
    };
  }, [login, logout]);

  // Show loading while checking authentication
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-orange-50 to-amber-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Đang kiểm tra phiên đăng nhập...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}